import React, { ChangeEvent, useEffect, useState } from "react";
import InputColor, { Color } from "react-input-color";
import owLogo from "../../../../assets/images/orderwerks/stacked-logo-grey.svg";
import ICreateBrandedLoginSubDomainDTO from "../../../../models/DTO/ICreateBrandedLoginSubDomainDTO";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import IOnCreateBrandedLoginSubDomainDTO from "../../../../models/DTO/IOnCreateBrandedLoginSubDomainDTO";
import IOnLoadUserDTO from "../../../../models/DTO/IOnLoadUserDTO";
import IOnProcessBrandedLoginDTO from "../../../../models/DTO/IOnProcessBrandedLoginDTO";
import IProcessBrandedLoginDTO from "../../../../models/DTO/IProcessBrandedLoginDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import FileUploadResourceTypeEnum from "../../../../models/FileUploadResourceTypeEnum";
import IBranded_Login from "../../../../models/IBranded_Login";
import IBranded_Login_Template from "../../../../models/IBranded_Login_Template";
import INotification from "../../../../models/INotification";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwS3Uploader from "../../../shared/OwS3Uploader/OwS3Uploader";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import "./BrandedLoginSidebar.scss";
import { Socket } from "socket.io-client";

type Props = {
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    toggleSidebar: () => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const BrandedLoginSidebar: React.FC<Props> = (props) => {
    const [subdomain, setSubdomain] = useState("");
    const [template, setTemplate] = useState<IBranded_Login_Template | undefined>(props.supplierModel.Branded_Login?.template);
    const [saveActive, setSaveActive] = useState<boolean>(false);
    const [brandedLogin, setBrandedLogin] = useState<IBranded_Login | undefined>(props.supplierModel.Branded_Login);

    useEffect(() => {
        props.socket.on(EndpointsEnum.SuppliersOnCreateBrandedLoginSubDomain, onCreateSubDomain);
        props.socket.on(EndpointsEnum.SuppliersOnProcessBrandedLogin, onProcessBrandedLogin);
        props.socket.on(EndpointsEnum.Notify, notify);
        props.socket.on(EndpointsEnum.AppOnLoadUser, onLoadUser);

        return () => {
            props.socket.removeListener(EndpointsEnum.SuppliersOnCreateBrandedLoginSubDomain, onCreateSubDomain);
            props.socket.removeListener(EndpointsEnum.SuppliersOnProcessBrandedLogin, onProcessBrandedLogin);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
            props.socket.removeListener(EndpointsEnum.AppOnLoadUser, onLoadUser);
        };
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        if (name === "subdomain") {
            setSubdomain(value);
        }
    };

    const notify = () => {
        setSaveActive(false);
    };

    const createSubDomain = () => {
        if (subdomain.length > 0) {
            const payload: ICreateBrandedLoginSubDomainDTO = {
                userGuid: props.userModel.guid,
                userToken: props.userModel.token,
                supplierGuid: props.supplierModel.guid,
                subdomain,
            };

            props.socket.emit(EndpointsEnum.SuppliersCreateBrandedLoginSubDomain, payload);

            setSaveActive(true);
        }
    };

    const onCreateSubDomain = (data: IOnCreateBrandedLoginSubDomainDTO) => {
        const notification: INotification = {
            message: "successfully_created_subdomain",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        setSaveActive(false);

        loadUser();
    };

    const handleColorChange = (color: string, property: string) => {
        if (template) {
            const existingTemplate = { ...template };

            switch (property) {
                case "main_bg":
                    existingTemplate.main.background_color = color;
                    break;

                case "header_bg":
                    existingTemplate.header.background_color = color;
                    break;

                case "header_color":
                    existingTemplate.header.color = color;
                    break;

                case "footer_bg":
                    existingTemplate.footer.background_color = color;
                    break;

                case "footer_color":
                    existingTemplate.footer.color = color;
                    break;

                case "login_bg":
                    existingTemplate.main.login.background_color = color;
                    break;

                case "login_color":
                    existingTemplate.main.login.color = color;
                    break;

                case "btn_bg":
                    existingTemplate.main.login.btn_background_color = color;
                    break;

                case "btn_color":
                    existingTemplate.main.login.btn_color = color;
                    break;

                case "link_color":
                    existingTemplate.main.login.link_color = color;
                    break;
            }

            setTemplate(existingTemplate);
        }
    };

    const saveBrandedLogin = () => {
        if (template) {
            const payload: IProcessBrandedLoginDTO = {
                userGuid: props.userModel.guid,
                userToken: props.userModel.token,
                supplierGuid: props.supplierModel.guid,
                template,
            };

            props.socket.emit(EndpointsEnum.SuppliersProcessBrandedLogin, payload);

            setSaveActive(true);
        }
    };

    const loadUser = () => {
        const payload: ILoadUserDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    const onLoadUser = (data: IOnLoadUserDTO) => {
        setTemplate(data.supplier.Branded_Login?.template);
        setBrandedLogin(data.supplier.Branded_Login);
    };

    const onProcessBrandedLogin = (data: IOnProcessBrandedLoginDTO) => {
        setSaveActive(false);

        const notification: INotification = {
            message: "successfully_updated_branded_login",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        loadUser();
    };

    const removeBrandedLogin = () => {
        if (template) {
            const payload: IProcessBrandedLoginDTO = {
                userGuid: props.userModel.guid,
                userToken: props.userModel.token,
                supplierGuid: props.supplierModel.guid,
                template,
                deleteBrandedLogin: true,
            };

            props.socket.emit(EndpointsEnum.SuppliersProcessBrandedLogin, payload);

            setSaveActive(true);
        }
    };

    return (
        <div className={"BrandedLoginSidebar p-2"}>
            <div className={"d-flex justify-content-between"}>
                <OwTitle title={"branded_login"} />
                <div>
                    <OwButton className={"btn btn-sm btn-danger"} centerIconClass={"fas fa-times"} fn={props.toggleSidebar} />
                </div>
            </div>

            {brandedLogin ? (
                <div>
                    <div>
                        <a href={`https://${brandedLogin.subdomain}.orderwerks.com`} target={"_blank"} rel="noreferrer">
                            <OwLabel title={"view_live"} className={"pointer"} leftIconClass={"fad fa-external-link-alt"} />
                        </a>
                    </div>
                    <div>URL: https://{brandedLogin.subdomain}.orderwerks.com</div>
                </div>
            ) : (
                <div>
                    <div>
                        <OwLabel title={"subdomain"} />
                    </div>
                    <div className={"mb-2"}>
                        <OwInputWrapper name={"subdomain"} fn={handleChange} value={subdomain} />
                    </div>
                    <div>
                        <OwButton title={"create"} fn={createSubDomain} active={saveActive} disableOnActive={true} />
                    </div>
                </div>
            )}
            {brandedLogin && template ? (
                <div style={{ overflowY: "scroll", height: "80vh", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <div className={"mb-3"}>
                        <OwTitle title={"logo"} tag={"h5"} />
                        <OwS3Uploader
                            resourceType={FileUploadResourceTypeEnum.brandedLoginLogo}
                            socket={props.socket}
                            isReadOnly={false}
                            userModel={props.userModel}
                            supplierModel={props.supplierModel}
                            imagesOnly={true}
                            isBrandedLogo={true}
                        />
                    </div>
                    <div className={"mb-3"}>
                        <OwTitle title={"header"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"color"} />
                            </div>
                            <InputColor
                                initialValue={template.header.color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "header_color");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <div>
                            <div>
                                <OwLabel title={"background"} />
                            </div>
                            <InputColor
                                initialValue={template.header.background_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "header_bg");
                                }}
                                placement={"bottom"}
                            />
                        </div>
                    </div>
                    <div className={"mb-3"}>
                        <OwTitle title={"main"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"background"} />
                            </div>
                            <InputColor
                                initialValue={template.main.background_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "main_bg");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <OwTitle title={"login"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"color"} />
                            </div>
                            <InputColor
                                initialValue={template.main.login.color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "login_color");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <div>
                            <div>
                                <OwLabel title={"background"} />
                            </div>
                            <InputColor
                                initialValue={template.main.login.background_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "login_bg");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <OwTitle title={"button"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"color"} />
                            </div>
                            <InputColor
                                initialValue={template.main.login.btn_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "btn_color");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <div>
                            <div>
                                <OwLabel title={"background"} />
                            </div>
                            <InputColor
                                initialValue={template.main.login.btn_background_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "btn_bg");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <OwTitle title={"link"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"color"} />
                            </div>
                            <InputColor
                                initialValue={template.main.login.link_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "link_color");
                                }}
                                placement={"bottom"}
                            />
                        </div>
                    </div>
                    <div className={"mb-3"}>
                        <OwTitle title={"footer"} tag={"h5"} />

                        <div>
                            <div>
                                <OwLabel title={"color"} />
                            </div>
                            <InputColor
                                initialValue={template.footer.color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "footer_color");
                                }}
                                placement={"bottom"}
                            />
                        </div>

                        <div>
                            <div>
                                <OwLabel title={"background"} />
                            </div>
                            <InputColor
                                initialValue={template.footer.background_color}
                                onChange={(color: Color) => {
                                    handleColorChange(color.hex, "footer_bg");
                                }}
                                placement={"bottom"}
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <OwLabel title={"preview"} />
                        </div>

                        <div className={"mini-preview"}>
                            <div className={"header"} style={{ backgroundColor: template.header.background_color }}></div>
                            <div className={"main"} style={{ backgroundColor: template.main.background_color }}>
                                <div className={"login"} style={{ backgroundColor: template.main.login.background_color }}>
                                    <div className={"logo"}></div>
                                    <div className={"inputs"}>
                                        <div className={"emailLabel"} style={{ color: template.main.login.color }}>
                                            Email
                                        </div>
                                        <div className={"emailInput"} />
                                        <div className={"passwordLabel"} style={{ color: template.main.login.color }}>
                                            Password
                                        </div>
                                        <div className={"passwordInput"} />
                                        <div
                                            className={"submit"}
                                            style={{
                                                backgroundColor: template.main.login.btn_background_color,
                                                color: template.main.login.btn_color,
                                            }}>
                                            Login
                                        </div>
                                    </div>
                                    <div className={"links"}>
                                        By logging in you accept the{" "}
                                        <span style={{ color: template.main.login.link_color }}>Terms of Use</span> and{" "}
                                        <span style={{ color: template.main.login.link_color }}>Privacy Policy</span>.
                                    </div>
                                    <div className={"service-status"} style={{ color: template.main.login.link_color }}>
                                        <i className={"fas fa-info-circle"} /> Service Status
                                    </div>
                                </div>
                            </div>
                            <div
                                className={"footer"}
                                style={{ backgroundColor: template.footer.background_color, color: template.footer.color }}>
                                <div className={"ow-logo"}>
                                    <div>Powered By</div>
                                    <img src={owLogo} />
                                </div>
                                <div className={"company"}>Company Name</div>
                                <div className={"contact"}>
                                    <div>123 Anywhere</div>
                                    <div>Unit A</div>
                                    <div>Anywhere, CA 92120</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={"mt-3"}>
                        <OwButton title={"save"} fn={saveBrandedLogin} active={saveActive} disableOnActive={true} />
                    </div>

                    <div className={"mt-5"}>
                        <OwButton title={"remove_branded_login"} className={"btn btn-danger"} fn={removeBrandedLogin} active={saveActive} />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
